import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  InputNumber,
  Button,
  Spin,
  message,
  Typography,
  Divider,
  Space,
  Alert,
  Empty,
  Tooltip,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  DollarOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useSettings } from "../../../hooks/finance/useSettings";

const { Title, Text, Paragraph } = Typography;

const AdmissionFeeManagement = () => {
  const {
    admissionFee,
    loading,
    fetchAdmissionFee,
    updateAdmissionFee,
    createAdmissionFee,
  } = useSettings();

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchAdmissionFee();
  }, []);

  useEffect(() => {
    if (admissionFee) {
      form.setFieldsValue({
        amount: parseFloat(admissionFee.value),
      });
    }
  }, [admissionFee, form]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      let success;
      if (admissionFee) {
        // Update existing setting - pass the ID explicitly
        success = await updateAdmissionFee(values.amount, admissionFee.id);
        if (success) {
          message.success("Admission fee updated successfully");
          await fetchAdmissionFee(); // Refresh to show updated value
        }
      } else {
        // Create new setting
        success = await createAdmissionFee(values.amount);
        if (success) {
          message.success("Admission fee created successfully");
          setIsCreating(false);
          await fetchAdmissionFee(); // Refresh to show the new setting
        }
      }
    } catch (error) {
      message.error(
        `Failed to ${admissionFee ? "update" : "create"} admission fee`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (admissionFee) {
      form.setFieldsValue({
        amount: parseFloat(admissionFee.value),
      });
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card bordered={false}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div>
                <Title level={3}>
                  <DollarOutlined /> Admission Fee Settings
                </Title>
                <Paragraph type="secondary">
                  Manage the default admission application fee. This fee is
                  charged to all applicants during the admission process.
                </Paragraph>
              </div>

              <Divider />

              {loading && !admissionFee && !isCreating ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <Spin size="large" tip="Loading admission fee settings..." />
                </div>
              ) : !admissionFee && !isCreating ? (
                <Empty
                  description={
                    <div>
                      <Text strong style={{ fontSize: "16px" }}>
                        No Admission Fee Configured
                      </Text>
                      <br />
                      <Text type="secondary">
                        The admission fee has not been set up yet. Click the
                        button below to create it.
                      </Text>
                    </div>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={() => setIsCreating(true)}
                  >
                    Create Admission Fee Setting
                  </Button>
                </Empty>
              ) : (
                <>
                  {admissionFee && !isCreating && (
                    <Alert
                      message="Current Admission Fee"
                      description={
                        <div>
                          <Text strong style={{ fontSize: "24px" }}>
                            UGX{" "}
                            {parseFloat(admissionFee.value).toLocaleString(
                              "en-UG",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Last updated:{" "}
                            {(() => {
                              try {
                                const dateStr =
                                  admissionFee.updated_at ||
                                  admissionFee.created_at;
                                const date = new Date(dateStr);
                                return isNaN(date.getTime())
                                  ? "N/A"
                                  : date.toLocaleString();
                              } catch {
                                return "N/A";
                              }
                            })()}
                          </Text>
                        </div>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: "24px" }}
                    />
                  )}

                  <Card
                    title={
                      admissionFee
                        ? "Update Admission Fee"
                        : "Create Admission Fee"
                    }
                    bordered={true}
                    style={{ maxWidth: "600px" }}
                    extra={
                      isCreating && (
                        <Button
                          type="link"
                          onClick={() => setIsCreating(false)}
                        >
                          Cancel
                        </Button>
                      )
                    }
                  >
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleSubmit}
                      autoComplete="off"
                    >
                      <Form.Item
                        label="Admission Fee Amount (UGX)"
                        name="amount"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the admission fee amount",
                          },
                          {
                            type: "number",
                            min: 0,
                            message:
                              "Amount must be greater than or equal to 0",
                          },
                        ]}
                        extra="This amount will be charged to all new applicants"
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Enter amount"
                          formatter={(value) =>
                            `UGX ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                          }
                          parser={(value) => value.replace(/UGX\s?|(,*)/g, "")}
                          precision={2}
                          min={0}
                          size="large"
                        />
                      </Form.Item>

                      <Form.Item>
                        <Space>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={
                              admissionFee ? <SaveOutlined /> : <PlusOutlined />
                            }
                            loading={saving}
                            size="large"
                          >
                            {admissionFee ? "Update Fee" : "Create Fee"}
                          </Button>
                          {admissionFee && (
                            <Tooltip title="Reset form to the current saved value">
                              <Button
                                onClick={handleReset}
                                icon={<ReloadOutlined />}
                                size="large"
                                disabled={saving}
                              >
                                Reset
                              </Button>
                            </Tooltip>
                          )}
                        </Space>
                      </Form.Item>
                    </Form>
                  </Card>

                  {(admissionFee || isCreating) && (
                    <Alert
                      message="Important Information"
                      description={
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          <li>
                            Changes take effect immediately for new applications
                          </li>
                          <li>
                            Existing pending applications will still use their
                            original fee
                          </li>
                        </ul>
                      }
                      type="warning"
                      showIcon
                    />
                  )}
                </>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdmissionFeeManagement;
